<template>
  <q-page class="q-pa-xl">
    <div class="row fit align-center items-center q-mb-md">
      <span
        id="dashboard_title"
        class="text-h4"
      >{{ i18n.title }}</span>
      <span class="text-h5 text-muted q-px-md">|</span>
      <q-select
        v-model="state_dashboards_selected"
        :options="dashboards"
        emit-value
        map-options
        option-value="link"
        option-label="label"
        class="dashboard-view-selector text-h4"
        style="width: fit-content"
        dense
        borderless
        flat
        :hide-dropdown-icon="dashboards.length === 1"
      />
    </div>

    <!-- Outdated data warning -->
    <q-banner
      v-if="needs_audit"
      class="warning-banner p-4 q-mb-md text-default"
      rounded
    >
      <template v-slot:avatar>
        <q-icon
          name="sym_o_report"
          size="md"
        />
      </template>
      <div class="text-body1">
        {{ i18n.outdated_warning }}
      </div>
      <template v-slot:action>
        <q-btn
          unelevated
          size="md"
          icon="auto_awesome"
          color="primary"
          :label="i18n.run_audit"
          @click="$inertia.visit(audit_path)"
        />
      </template>
    </q-banner>

    <!-- Filters -->
    <div class="row justify-start items-start content-start align-start q-pt-md q-gutter-sm">
      <q-select
        v-model="selectedAccounts"
        :options="filteredAccounts"
        multiple
        outlined
        dense
        options-dense
        transition-show="none"
        transition-hide="none"
        hide-bottom-space
        option-value="account_num"
        :option-label="accountOptionLabel"
        clearable
        popup-content-class="q-pa-sm"
        style="max-width: fit-content; width: auto; min-width: 260px"
        class="select-filter q-mr-sm q-mb-sm"
        :class="{
          'select-filter-active': selectedAccounts.length > 0,
        }"
        @update:model-value="onAccountsSelect"
        @filter="filterAccounts"
      >
        <template v-slot:selected>
          <div>
            {{ selectedAccounts.length
              ? `${i18n.select_accounts} (${selectedAccounts.length})`
              : i18n.select_accounts }}
          </div>
        </template>
        <template v-slot:option="scope">
          <q-item 
            v-bind="scope.itemProps"
            :class="{ 'text-accent text-bold': selectedAccounts.find((a) => a.account_num === scope.opt.account_num) }"
          >
            <q-item-section>
              <q-item-label>{{ scope.opt.account_num }} - {{ scope.opt.account_lib }}</q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>

      <template v-if="selectedAccounts.length">
        <q-chip
          v-for="account in selectedAccounts"
          :key="account.account_num"
          removable
          size="14px"
          text-color="primary"
          class="filter-chip bg-override q-ml-xs q-field__control relative-position row no-wrap text-primary"
          @remove="removeAccount(account)"
        >
          <q-tooltip
            anchor="bottom middle"
            self="top middle"
            :delay="300"
          >
            {{ account.account_lib }}
          </q-tooltip>
          {{ account.account_num }}
        </q-chip>
      </template>

      <q-select
        outlined
        dense
        transition-show="none"
        transition-hide="none"
        options-dense
        hide-bottom-space
        :options="[]"
        :model-value="date_range_label"
        :prefix="i18n.period_label + ':'"
        style="max-width: fit-content; width: 100%"
        :class="{
          'select-filter': true,
          'select-filter-active': date_range_label,
          'q-mr-sm': true,
          'q-mb-sm': true
        }"
        clearable
        @clear="clearDateRange"
      >
        <template v-slot:no-option>
          <div
            class="row q-pa-none"
            style="max-width: 360px; width: 100%"
          >
            <q-date
              :model-value="date_range"
              minimal
              flat
              range
              color="accent"
              text-color="default"
              class="fit"
              @update:model-value="selectDateRange"
            />
          </div>

          <q-separator v-if="workspace_settings.accounting_end_day" />

          <div
            v-if="workspace_settings.accounting_end_day"
            class="row justify-start q-pa-md q-pb-none"
          >
            <div class="col-12">
              <p class="text-bold">
                {{ i18n.dates_fiscal_year }}
              </p>
            </div>
            <div class="col-12">
              <q-btn
                v-for="fy in fiscal_years"
                :key="fy.label"
                class="q-mr-md"
                :label="fy.label"
                color="primary"
                :outline="!isFiscalYearActive(fy)"
                @click="selectFiscalYear(fy)"
              />
            </div>
          </div>

          <div class="row justify-end q-px-md q-pb-md">
            <div class="col-shrink">
              <q-btn
                v-close-popup
                :label="$q.lang.label.close"
                unelevated
                color="primary"
                flat
                no-caps
                class="btn-underline"
              />
            </div>
          </div>
        </template>
      </q-select>

      <q-space />

      <!-- Date -->
      <DatePicker
        v-if="selectedAccounts.length > 0 && balance_history_first"
        :min-date="balance_history_first"
        :max-date="day"
        :selected-date="date_selected"
        :tooltip-next="i18n.day_next"
        :tooltip-previous="i18n.day_previous"
        @update:selected-date="on_date_picker_click($event)"
      />
    </div>

    <!-- Graph Balance History -->
    <template v-if="selectedAccounts.length > 0">
      <div class="row full_width q-col-gutter-lg q-pt-md">
        <div class="col-12">
          <q-card class="fit">
            <q-card-section class="row items-center justify-space-between q-pt-sm q-pb-none">
              <div class="col-shrink align-center justify-center item-center q-card-title q-py-sm gt-xs row items-center no-wrap">
                <span>{{ i18n.balance }}</span>
                <span class="text-muted text-caption q-ml-sm">
                  &middot; {{ formatted_date }} ({{ i18n.at }} {{ formatted_time }})
                </span>
              </div>
              <div class="col text-right">
                <q-btn-toggle
                  v-model="balance_timerange"
                  unelevated
                  class="date-range-btn"
                  size="sm"
                  toggle-color="primary"
                  :options="balance_timeranges"
                />
              </div>
            </q-card-section>
            <q-card-section class="q-pt-none">
              <!-- Balance -->
              <q-item class="q-pa-none">
                <q-item-section>
                  <q-item-label class="">
                    <span
                      class="text-bold"
                      style="font-size: min(4vw, 28px); font-family: 'Inconsolata', monospace; font-weight: 600"
                    >
                      {{ formatAmount(balance_at_date) }} {{ fiat }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
              <!-- /Balance -->
            </q-card-section>
            <q-card-section
              v-if="balance_history.length"
              class="q-pb-md q-pt-none"
            >
              <Line
                ref="history_graph_ref"
                :data="chart_data"
                :options="chart_options"
                :height="270"
              />
            </q-card-section>
          </q-card>
        </div>
      </div>

      <!-- Period summary -->
      <div class="row q-mt-md">
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">
                {{ i18n.period_summary }}
              </div>
              <div
                v-if="start_date && end_date"
                class="row q-mt-md q-col-gutter-md items-stretch"
              >
                <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                  <q-card
                    flat
                    class="bg-surface period-summary-card full-height"
                  >
                    <q-card-section class="column items-center text-center q-gutter-xs">
                      <q-icon
                        name="sym_o_login"
                        size="24px"
                        class="text-primary"
                      />
                      <div class="text-caption text-grey">
                        {{ i18n.start_balance }}
                      </div>
                      <div class="text-h5 period-summary-value">
                        {{ formatAmount(period_start_balance) }} {{ fiat }}
                      </div>
                    </q-card-section>
                  </q-card>
                </div>
                <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                  <q-card
                    flat
                    class="bg-surface period-summary-card full-height"
                  >
                    <q-card-section class="column items-center text-center q-gutter-xs">
                      <q-icon
                        name="sym_o_logout"
                        size="24px"
                        class="text-primary"
                      />
                      <div class="text-caption text-grey">
                        {{ i18n.end_balance }}
                      </div>
                      <div class="text-h5 period-summary-value">
                        {{ formatAmount(period_end_balance) }} {{ fiat }}
                      </div>
                    </q-card-section>
                  </q-card>
                </div>
                <div class="col-12 col-md-4 col-lg-4 col-xl-4">
                  <q-card
                    flat
                    class="bg-surface period-summary-card full-height"
                  >
                    <q-card-section class="column items-center text-center q-gutter-xs">
                      <q-icon
                        name="trending_up"
                        size="24px"
                        class="text-primary"
                      />
                      <div class="text-caption text-grey">
                        {{ i18n.variation }}
                      </div>
                      <div
                        class="text-h5 period-summary-value"
                        :class="period_variation >= 0 ? 'text-positive' : 'text-negative'"
                      >
                        {{ formatAmount(period_variation) }} {{ fiat }}
                      </div>
                    </q-card-section>
                  </q-card>
                </div>
              </div>
              <div
                v-else
                class="row q-mt-md"
              >
                <div class="col text-center text-grey">
                  {{ i18n.select_period }}
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>

      <!-- Entries table -->
      <div class="row q-mt-md">
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">
                {{ i18n.accounting_entries }}
              </div>
            </q-card-section>
            <q-card-section class="q-pa-none">
              <q-table
                :rows="getEntriesForDate()"
                :columns="columns"
                row-key="id"
                flat
                dense
                class="app-table"
                separator="none"
                :rows-per-page-options="[50, 100, 200, 0]"
                :pagination="{ rowsPerPage: 50, sortBy: 'date', descending: true }"
              >
                <template v-slot:body-cell-date="props">
                  <q-td :props="props">
                    <a
                      :href="getTransactionsLinkForDate(props.row.date)"
                      class="table-link"
                    >
                      {{ formatDateOnly(props.row.date) }}
                    </a>
                  </q-td>
                </template>
                <template v-slot:body-cell-account_num="props">
                  <q-td :props="props">
                    {{ props.row.account_num }} - {{ account_names_map[props.row.account_num] }}
                  </q-td>
                </template>
                <template v-slot:body-cell-counterpart_account="props">
                  <q-td :props="props">
                    {{ props.row.counterpart_account }} - {{ props.row.counterpart_account_lib }}
                  </q-td>
                </template>
                <template v-slot:body-cell-debit="props">
                  <q-td
                    :props="props"
                    class="text-right"
                  >
                    <span v-if="props.row.debit > 0">{{ formatAmount(props.row.debit) }}</span>
                  </q-td>
                </template>
                <template v-slot:body-cell-credit="props">
                  <q-td
                    :props="props"
                    class="text-right"
                  >
                    <span v-if="props.row.credit > 0">{{ formatAmount(props.row.credit) }}</span>
                  </q-td>
                </template>
                <template v-slot:body-cell-net="props">
                  <q-td
                    :props="props"
                    class="text-right text-bold"
                    :class="(props.row.credit - props.row.debit) >= 0 ? 'text-positive' : 'text-negative'"
                  >
                    {{ formatAmount(props.row.credit - props.row.debit) }}
                  </q-td>
                </template>
              </q-table>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </template>
    <div
      v-else
      class="row justify-center q-pa-xl"
    >
      <div class="col-12 text-center">
        {{ i18n.select_accounts }}
      </div>
    </div>
  </q-page>
</template>

<script>
import DatePicker from './date_picker.vue'
import Layout from '../../../layouts/workspace.vue'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LineElement, Tooltip, LinearScale, PointElement, TimeScale, Filler } from 'chart.js'
import { CrosshairPlugin } from 'chartjs-plugin-crosshair'
import annotationPlugin from 'chartjs-plugin-annotation'
import zoomPlugin from 'chartjs-plugin-zoom'
import 'chartjs-adapter-date-fns'
import { date as qdate } from 'quasar'

const CustomCrosshairPlugin = function (plugin) {
  const originalAfterDraw = plugin.afterDraw
  plugin.afterDraw = function (chart, easing) {
    if (chart && chart.crosshair) {
      originalAfterDraw.call(this, chart, easing)
    }
  }
  return plugin
}

ChartJS.register(
  CategoryScale,
  LineElement,
  Tooltip,
  LinearScale,
  PointElement,
  TimeScale,
  Filler,
  annotationPlugin,
  zoomPlugin,
  CustomCrosshairPlugin(CrosshairPlugin)
)

export default {
  components: {
    DatePicker,
    Line
  },
  layout: Layout,
  props: {
    i18n: Object,
    fiat: String,
    dashboards: Array,
    dashboards_selected: String,
    accounting_accounts: Array,
    dashboard_data: Object,
    needs_audit: Boolean,
    audit_path: String,
    day: String,
    workspace_settings: Object,
    fiscal_years: Array
  },
  data() {
    let chart_width
    let chart_height
    let chart_fill_gradient

    const defaultAccounts = this.accounting_accounts.filter(acc => {
      const firstChar = acc.account_num.charAt(0)
      return firstChar === '6' || firstChar === '7'
    })

    return {
      state_dashboards_selected: this.dashboards_selected,
      selectedAccounts: defaultAccounts,
      filteredAccounts: this.accounting_accounts,
      balance_timerange: 'all',
      date_selected: this.day,
      start_date: null,
      end_date: null,
      balance_history_first: null,
      balance_history_last: null,
      chart_width,
      chart_height,
      chart_fill_gradient
    }
  },
  computed: {
    columns() {
      return [
        { name: 'date', label: this.i18n.table_date, field: 'date', align: 'left', sortable: true },
        { name: 'account_num', label: this.i18n.table_account, field: 'account_num', align: 'left' },
        { name: 'transaction_kind', label: this.i18n.table_type, field: 'transaction_kind', align: 'left' },
        { name: 'counterpart_account', label: this.i18n.table_counterpart, field: 'counterpart_account', align: 'left' },
        { name: 'debit', label: this.i18n.table_debit, field: 'debit', align: 'right', sortable: true },
        { name: 'credit', label: this.i18n.table_credit, field: 'credit', align: 'right', sortable: true },
        { name: 'net', label: this.i18n.table_net, field: 'net', align: 'right', sortable: true },
        { name: 'note', label: this.i18n.table_note, field: 'note', align: 'left' }
      ]
    },
    date_range() {
      if (this.start_date && this.end_date) {
        return {
          from: this.start_date,
          to: this.end_date
        }
      }
      return null
    },
    date_range_label() {
      if (!this.start_date || !this.end_date) return ''
      const format = 'DD/MM/YY'
      const from = qdate.formatDate(this.start_date, format)
      const to = qdate.formatDate(this.end_date, format)
      if (this.start_date === this.end_date) {
        return from
      }
      return `${from} - ${to}`
    },
    account_names_map() {
      const map = {}
      if (this.dashboard_data?.accounts) {
        this.dashboard_data.accounts.forEach(acc => {
          map[acc.account_num] = acc.account_lib
        })
      }
      return map
    },
    balance_timeranges() {
      return [
        { label: this.i18n.time_range_1_week, value: '1w' },
        { label: this.i18n.time_range_1_month, value: '1m' },
        { label: this.i18n.time_range_3_months, value: '3m' },
        { label: this.i18n.time_range_ytd, value: 'ytd' },
        { label: this.i18n.time_range_all, value: 'all' }
      ]
    },
    period_start_balance() {
      if (!this.selectedAccounts.length || !this.dashboard_data?.entries_by_account) return null
      if (!this.start_date) return null
      
      const startDate = new Date(this.start_date)
      let balance = 0
      
      this.selectedAccounts.forEach(account => {
        const entries = this.dashboard_data.entries_by_account[account.account_num] || []
        entries.forEach(entry => {
          const entryDate = new Date(entry.date)
          if (entryDate < startDate) {
            balance += (parseFloat(entry.credit) || 0) - (parseFloat(entry.debit) || 0)
          }
        })
      })
      
      return balance
    },
    period_end_balance() {
      if (!this.selectedAccounts.length || !this.dashboard_data?.entries_by_account) return null
      if (!this.end_date) return null
      
      const endDate = new Date(this.end_date)
      endDate.setHours(23, 59, 59, 999)
      let balance = 0
      
      this.selectedAccounts.forEach(account => {
        const entries = this.dashboard_data.entries_by_account[account.account_num] || []
        entries.forEach(entry => {
          const entryDate = new Date(entry.date)
          if (entryDate <= endDate) {
            balance += (parseFloat(entry.credit) || 0) - (parseFloat(entry.debit) || 0)
          }
        })
      })
      
      return balance
    },
    period_variation() {
      if (this.period_start_balance === null || this.period_end_balance === null) return null
      return this.period_end_balance - this.period_start_balance
    },
    balance_history() {
      if (!this.selectedAccounts.length || !this.dashboard_data?.entries_by_account) return []
      
      const allEntries = this.getAllEntriesForAccounts()
      if (allEntries.length === 0) return []

      // Group by date and calculate cumulative balance
      const balancesByDate = {}
      let cumulativeBalance = 0

      allEntries.forEach(entry => {
        const date = entry.date.split('T')[0]
        if (!balancesByDate[date]) {
          balancesByDate[date] = 0
        }
        const credit = parseFloat(entry.credit) || 0
        const debit = parseFloat(entry.debit) || 0
        balancesByDate[date] += credit - debit
      })

      // Create array of [date, balance]
      const history = []
      Object.keys(balancesByDate).sort().forEach(date => {
        cumulativeBalance += balancesByDate[date]
        history.push([date, cumulativeBalance])
      })

      // Add starting point (day before first entry with balance 0)
      if (history.length > 0) {
        const firstDate = new Date(history[0][0])
        firstDate.setDate(firstDate.getDate() - 1)
        history.unshift([firstDate.toISOString().split('T')[0], 0])
      }

      return history
    },
    filtered_balance_history() {
      const cutoffDate = this.getCutoffDate()
      if (!cutoffDate) return this.balance_history
      return this.balance_history.filter(h => new Date(h[0]) >= cutoffDate)
    },
    date_annotation() {
      const selectedDate = new Date(this.date_selected)
      return {
        type: 'line',
        borderColor: 'rgba(0, 0, 0, 0.4)',
        borderDash: [6, 6],
        borderWidth: 1,
        label: {
          display: false
        },
        scaleID: 'x',
        value: selectedDate.getTime()
      }
    },
    balance_at_date() {
      if (!this.balance_history.length) return 0
      
      const targetDate = new Date(this.date_selected).getTime()
      let balance = 0
      
      for (const [date, bal] of this.balance_history) {
        const entryDate = new Date(date).getTime()
        if (entryDate <= targetDate) {
          balance = bal
        } else {
          break
        }
      }
      
      return balance
    },
    formatted_date() {
      return qdate.formatDate(this.date_selected, 'DD/MM/YYYY')
    },
    formatted_time() {
      return qdate.formatDate(this.date_selected, 'HH:mm')
    },
    chart_data() {
      return {
        datasets: [
          {
            label: 'Balance',
            data: this.filtered_balance_history,
            borderColor: 'rgb(111, 142, 236)',
            borderWidth: 2,
            backgroundColor: (context) => {
              const chart = context.chart
              const { ctx, chartArea } = chart
              if (!chartArea) return
              
              const current_width = chartArea.right - chartArea.left
              const current_height = chartArea.bottom - chartArea.top
              
              if (!this.chart_fill_gradient || this.chart_width !== current_width || this.chart_height !== current_height) {
                this.chart_width = current_width
                this.chart_height = current_height
                this.chart_fill_gradient = ctx.createLinearGradient(0, 0, 0, current_height)
                this.chart_fill_gradient.addColorStop(0, 'rgba(111, 142, 236, 0.2)')
                this.chart_fill_gradient.addColorStop(1, 'rgba(111, 142, 236, 0)')
              }
              
              return this.chart_fill_gradient
            },
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 6,
            tension: 0.4
          }
        ]
      }
    },
    chart_options() {
      let minDate = Infinity
      let maxDate = Infinity
      
      if (this.filtered_balance_history.length) {
        minDate = new Date(this.filtered_balance_history[0][0]).getTime()
        maxDate = new Date(this.filtered_balance_history[this.filtered_balance_history.length - 1][0]).getTime()
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: false
          },
          x: {
            min: minDate,
            max: maxDate,
            type: 'time',
            time: {
              unit: 'day',
              parser: 'yyyy-MM-dd',
              displayFormats: {
                day: 'dd/MM/yy'
              }
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10
            },
            grid: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          annotation: {
            annotations: {
              currentDateAnnotation: this.date_annotation
            }
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            backgroundColor: '#ffffff',
            titleColor: '#000000',
            bodyColor: '#000000',
            padding: 12,
            caretSize: 0,
            caretPadding: 64,
            borderColor: '#eee',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            intersect: false,
            usePointStyle: true,
            callbacks: {
              labelPointStyle: function() {
                return {
                  pointStyle: 'circle',
                  rotation: 0
                }
              },
              title: (tooltipItems) => {
                return qdate.formatDate(tooltipItems[0].parsed.x, 'DD/MM/YYYY')
              },
              label: (tooltipItem) => {
                return `${this.i18n.balance}: ${this.formatAmount(parseFloat(tooltipItem.raw[1]))} ${this.fiat}`
              }
            }
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: false
              },
              mode: 'x',
              scaleMode: 'x'
            },
            pan: {
              enabled: true,
              mode: 'x'
            },
            limits: {
              x: {
                minRange: 5 * 24 * 60 * 60 * 1000,
                min: minDate,
                max: maxDate
              }
            }
          },
          crosshair: {
            line: {
              color: '#ddd',
              width: 1
            },
            sync: {
              enabled: false
            },
            zoom: {
              enabled: false
            }
          }
        },
        onClick: this.on_chart_click,
        interaction: {
          mode: 'nearest',
          intersect: false,
          axis: 'x'
        }
      }
    }
  },
  watch: {
    state_dashboards_selected(newVal) {
      if (newVal !== this.dashboards_selected) this.$inertia.visit(newVal)
    },
    selectedAccounts() {
      this.updateDateBounds()
    },
    balance_timerange() {
      // Reset zoom when changing time range
      if (this.$refs.history_graph_ref?.chart) {
        this.$refs.history_graph_ref.chart.resetZoom()
      }
    },
    start_date() {
      // Recalculate when period changes
      this.updateDateBounds()
    },
    end_date() {
      // Recalculate when period changes
      this.updateDateBounds()
    }
  },
  methods: {
    accountOptionLabel(option) {
      if (!option) return ''
      return `${option.account_num} - ${option.account_lib}`
    },
    onAccountsSelect(val) {
      this.selectedAccounts = val || []
    },
    removeAccount(account) {
      this.selectedAccounts = this.selectedAccounts.filter(acc => acc.account_num !== account.account_num)
    },
    getAllEntriesForAccounts() {
      if (!this.selectedAccounts.length || !this.dashboard_data?.entries_by_account) return []
      
      const startDate = this.start_date ? new Date(this.start_date) : null
      const endDate = this.end_date ? new Date(this.end_date) : null
      if (endDate) endDate.setHours(23, 59, 59, 999)
      
      const allEntries = []
      this.selectedAccounts.forEach(account => {
        const entries = this.dashboard_data.entries_by_account[account.account_num] || []
        entries.forEach(entry => {
          const entryDate = new Date(entry.date)
          
          // Filter by date range
          if (startDate && entryDate < startDate) return
          if (endDate && entryDate > endDate) return
          
          allEntries.push({ 
            ...entry, 
            account_num: account.account_num, 
            id: `${entry.movement_id || Math.random()}_${account.account_num}_${entry.date}_${entry.credit}_${entry.debit}` 
          })
        })
      })
      
      return allEntries.sort((a, b) => new Date(a.date) - new Date(b.date))
    },
    getEntriesForDate() {
      const allEntries = this.getAllEntriesForAccounts()
      const targetDate = new Date(this.date_selected)
      targetDate.setHours(23, 59, 59, 999)
      
      return allEntries.filter(entry => new Date(entry.date) <= targetDate)
    },
    getCutoffDate() {
      const now = new Date()
      switch (this.balance_timerange) {
      case '1w': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case '1m': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      case '3m': return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      case 'ytd': return new Date(now.getFullYear(), 0, 1)
      default: return null
      }
    },
    updateDateBounds() {
      if (this.balance_history.length > 0) {
        this.balance_history_first = this.balance_history[0][0]
        this.balance_history_last = this.balance_history[this.balance_history.length - 1][0]
        
        // Set date to today (to see current balance)
        this.date_selected = this.day
      } else {
        this.balance_history_first = null
        this.balance_history_last = null
      }
    },
    on_date_picker_click(date) {
      this.date_selected = date
    },
    on_chart_click(event, elements) {
      if (elements.length > 0) {
        const element = elements[0]
        const dataIndex = element.index
        const clickedDate = this.filtered_balance_history[dataIndex][0]
        this.date_selected = clickedDate
      }
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('fr-FR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount)
    },
    formatDate(dateStr) {
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('fr-FR', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      }).format(date)
    },
    formatDateOnly(dateStr) {
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('fr-FR', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit'
      }).format(date)
    },
    getTransactionsLinkForDate(dateStr) {
      const date = new Date(dateStr)
      const dateFormatted = encodeURIComponent(qdate.formatDate(date, 'YYYY/MM/DD'))
      const workspaceId = window.location.pathname.split('/')[2]
      return `/w/${workspaceId}/transactions?ajax_bar_skip=&order_by=time&order_direction=desc&date_from=${dateFormatted}&date_to=${dateFormatted}`
    },
    filterAccounts(val, update) {
      if (val === '') {
        update(() => {
          this.filteredAccounts = this.accounting_accounts
        })
        return
      }

      update(() => {
        const needle = val.toLowerCase()
        this.filteredAccounts = this.accounting_accounts.filter(acc => {
          const accountNum = acc.account_num.toLowerCase()
          const accountLib = acc.account_lib.toLowerCase()
          return accountNum.includes(needle) || accountLib.includes(needle)
        })
      })
    },
    selectDateRange(range) {
      if (!range) {
        this.start_date = null
        this.end_date = null
        return
      }

      if (range.from) {
        this.start_date = range.from
        this.end_date = range.to
      } else {
        this.start_date = range
        this.end_date = range
      }
    },
    clearDateRange() {
      this.start_date = null
      this.end_date = null
    },
    selectFiscalYear(fy) {
      this.start_date = qdate.formatDate(fy.date_start, 'YYYY-MM-DD')
      this.end_date = qdate.formatDate(fy.date_stop, 'YYYY-MM-DD')
    },
    isFiscalYearActive(fy) {
      const start = qdate.formatDate(fy.date_start, 'YYYY-MM-DD')
      const stop = qdate.formatDate(fy.date_stop, 'YYYY-MM-DD')
      return this.start_date === start && this.end_date === stop
    }
  }
}
</script>

<style scoped>
.dashboard-view-selector { cursor: pointer; }
.table-link {
  text-decoration: none;
  color: var(--text-primary);
}
.table-link:hover {
  text-decoration: underline;
}
.filter-chip {
  background-color: var(--select-filter-active-background) !important;
  border: 1px solid var(--select-filter-active-border);
  font-size: 14px;
  font-weight: 400;
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px !important;
  margin: 8px;
}

:deep(.filter-chip .q-chip__icon--remove) {
  font-size: 24px;
  opacity: 0.6;
  cursor: pointer;
  margin-right: 4px;
  margin-left: 8px;
  color: var(--text-primary);
}
:deep(.filter-chip .q-chip__icon--remove:hover) {
  opacity: 1;
}
.period-summary-value {
  font-family: 'Inconsolata', monospace;
  font-weight: 600;
}
.period-summary-card {
  border-radius: 10px;
  border: none;
  height: 100%;
}
</style>
